import { PencilIcon, TrashIcon } from "@heroicons/react/20/solid";
import ButtonWithPopoverDots from "components/buttons/ButtonWithPopoverDots";
import { useState } from "react";
import { createSearchParams, generatePath, NavLink } from "react-router-dom";

import AddressDisplay from "~/components/address/AddressDisplay.tsx";
import BreadcrumbsWrapper from "~/components/BreadcrumbsV2";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement";
import Card from "~/components/Card";
import CardValue from "~/components/Card/components/CardValue.tsx";
import CompanyLogo from "~/components/CompanyLogo";
import ContentWrapper from "~/components/ContentWrapper";
import Headline from "~/components/Headline";
import MainContent from "~/components/mainContent/MainContent";
import Section from "~/components/sections/Section";
import { CLIENT_DETAILS_PATH, CLIENTS_INDEX_PATH, COMPANY_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type {
	CentralInvoiceRecipient,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientTypes.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import {
	CLIENT_DETAILS_VIEW_TAB_ID_PARAM_NAME,
	ClientDetailsViewTabId,
} from "~/modules/client/components/ClientDetailsView/clientDetailsViewUtils.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

import DeleteCentralInvoiceRecipientModal from "./components/DeleteCentralInvoiceRecipientModal";
import UpdateCentralInvoiceRecipientSidebar from "./components/UpdateCentralInvoiceRecipientSidebar";

interface Props {
	centralInvoiceRecipient: CentralInvoiceRecipient;
	client: Client;
	company: Company;

}

const invoiceRecipientsTabTabSearchParams = {
	[CLIENT_DETAILS_VIEW_TAB_ID_PARAM_NAME]: ClientDetailsViewTabId.INVOICE_RECIPIENTS,
};

const CentralInvoiceRecipientDetailsView: React.FC<Props> = ({
	centralInvoiceRecipient,
	client,
	company,
}) => {
	const [showUpdateForm, setShowUpdateForm] = useState(false);
	const [showDeletionModal, setShowDeletionModal] = useState(false);

	return (
		<MainContent>
			<Section className="bg-white">
				<BreadcrumbsWrapper>
					<BreadcrumbElement text="Kunden"
									   url={CLIENTS_INDEX_PATH} />
					<BreadcrumbElement text={company.displayName}
									   url={generatePath(COMPANY_DETAILS_PATH, { companyId: company.id })} />
					<BreadcrumbElement text="Firmen"
									   url={CLIENTS_INDEX_PATH} />
					<BreadcrumbElement text={client.displayName}
									   url={generatePath(CLIENT_DETAILS_PATH, { clientId: client.id })}
									   searchParams={invoiceRecipientsTabTabSearchParams}
					/>
					<BreadcrumbElement text="Zentrale Rechnungsempfänger"
					/>
				</BreadcrumbsWrapper>
			</Section>
			<Section className="bg-white pb-2">
				<div className="flex w-full justify-between py-2">
					<div className="flex items-center gap-x-4">
						{company.avatarImage && <CompanyLogo image={company.avatarImage} />}
						<div className="flex flex-col">
							<Headline type="h2">{centralInvoiceRecipient.displayName}</Headline>
							<div className="flex items-center text-sm text-gray-500">
								<span className="mr-1">Zentraler Rechnungsempfänger | </span>
								<NavLink to={{
									pathname: generatePath(CLIENT_DETAILS_PATH, { clientId: client.id }),
									search: createSearchParams(invoiceRecipientsTabTabSearchParams).toString(),
								}}
										 className="hover:text-secondary-500">
									{client.displayName}
								</NavLink>
							</div>
						</div>
					</div>
					<ButtonWithPopoverDots
						theme="dark"
						items={[
							{
								label: "Bearbeiten",
								onClick: () => setShowUpdateForm(true),
								icon: PencilIcon,
							},
							{
								label: "Löschen",
								onClick: () => setShowDeletionModal(true),
								icon: TrashIcon,
							},
						]}
					/>
				</div>
			</Section>

			<ContentWrapper className="mt-8 grid w-full grid-cols-2 gap-x-6">
				<div className="space-y-4">
					<Card title="Abrechnungsdetails">
						<div className="grid grid-cols-2 gap-4">
							<CardValue label="Versand per">
								{centralInvoiceRecipient.invoiceByMail ? "E-Mail" : "Post"}
							</CardValue>
							<CardValue label="E-Mail für Rechnungsversand"
									   enableCopyToClipboard={!!centralInvoiceRecipient.invoiceEmail}>
								{centralInvoiceRecipient.invoiceEmail || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
						</div>
					</Card>
					<Card title="Rechnungskopf">
						{centralInvoiceRecipient.legalName || centralInvoiceRecipient.address ? (
							<div>
								{centralInvoiceRecipient.legalName && <>
									<span className="whitespace-pre-line">{nl2br(centralInvoiceRecipient.legalName)}</span><br /></>}
								{centralInvoiceRecipient.address &&
									<AddressDisplay address={centralInvoiceRecipient.address} />}
							</div>
						) : EMPTY_VALUE_PLACEHOLDER}
					</Card>
					<Card title="Anmerkungen">
						<p className="whitespace-pre-line">
							{centralInvoiceRecipient.comment ? nl2br(centralInvoiceRecipient.comment) : EMPTY_VALUE_PLACEHOLDER}
						</p>
					</Card>
				</div>
				<div>
					<Card title="Support Kontakt">
						<div className="grid grid-cols-2 gap-4">
							<div className="col-span-2">
								<CardValue label="Name Kontaktperson">
									{centralInvoiceRecipient.supportContactName || EMPTY_VALUE_PLACEHOLDER}
								</CardValue>
							</div>
							<CardValue label="Telefon"
									   enableCopyToClipboard={!!centralInvoiceRecipient.supportContactPhone}>
								{centralInvoiceRecipient.supportContactPhone || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
							<CardValue label="E-Mail"
									   enableCopyToClipboard={!!centralInvoiceRecipient.supportContactEmail}>
								{centralInvoiceRecipient.supportContactEmail || EMPTY_VALUE_PLACEHOLDER}
							</CardValue>
						</div>
					</Card>
				</div>
			</ContentWrapper>


			<UpdateCentralInvoiceRecipientSidebar
				invoiceRecipientData={centralInvoiceRecipient}
				isOpen={showUpdateForm}
				onClose={() => setShowUpdateForm(false)} />

			<DeleteCentralInvoiceRecipientModal clientId={centralInvoiceRecipient.clientId}
												invoiceRecipientDisplayName={centralInvoiceRecipient.displayName}
												invoiceRecipientId={centralInvoiceRecipient.id}
												isOpen={showDeletionModal}
												onClose={() => setShowDeletionModal(false)} />


		</MainContent>
	);
};

export default CentralInvoiceRecipientDetailsView;
